import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Armchair, Buildings, Palette } from "@phosphor-icons/react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = { residential: Armchair, commercial: Buildings, custom: Palette };
const categoryKeys = ["residential", "commercial", "custom"];

const images = {
  residential: "https://images.unsplash.com/photo-1767050321604-a2654be8fad0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzb2ZhJTIwbGl2aW5nJTIwcm9vbSUyMGVsZWdhbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzYyMTAxMTd8MA&ixlib=rb-4.1.0&q=85",
  commercial: "https://images.unsplash.com/photo-1671713682194-764a7c8dbc71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjByZXN0YXVyYW50JTIwYm9vdGglMjB1cGhvbHN0ZXJ5fGVufDB8fHx8MTc3NjIxMDExN3ww&ixlib=rb-4.1.0&q=85",
  custom: "https://images.unsplash.com/photo-1554046278-b219059d2e6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxjdXN0b20lMjB1cGhvbHN0ZXJ5JTIwZmFicmljJTIwdHVmdGVkJTIwZnVybml0dXJlJTIwd29ya3Nob3B8ZW58MHx8fHwxNzc2MjEwMTE3fDA&ixlib=rb-4.1.0&q=85",
};

export default function ServicesSection() {
  const { t } = useLanguage();

  const services = categoryKeys.map((key, i) => ({
    id: `s${i + 1}`,
    category: key,
    title: t.services[key].title,
    description: t.services[key].description,
    features: t.services[key].features,
    image: images[key],
  }));

  return (
    <section id="servicios" className="py-24 md:py-32 bg-[#050505]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700] mb-4 font-light">{t.services.overline}</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl font-light text-white tracking-tight">
            {t.services.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => {
            const Icon = iconMap[service.category] || Palette;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className={`group relative overflow-hidden bg-[#121212] border border-white/10 ${
                  i === 0 ? "md:col-span-2 lg:col-span-2" : ""
                }`}
                data-testid={`service-card-${service.category}`}
              >
                <div className="relative h-64 md:h-72 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon size={24} weight="duotone" className="text-[#FFD700]" />
                    <h3 className="font-['Cormorant_Garamond'] text-xl md:text-2xl font-light text-white">{service.title}</h3>
                  </div>
                  <p className="text-sm text-zinc-400 font-light leading-relaxed mb-5">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((f) => (
                      <span key={f} className="text-xs px-3 py-1 border border-white/10 text-zinc-400 uppercase tracking-wider">{f}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
